import {
  useChannelTemplates,
  useChannelType,
  useSendCommunication,
} from "@features/cx/consumerManagement/hooks";
import { useServiceOrders } from "@features/cx/shared/hooks/useServiceOrder";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  CheckCircle,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Settings2Icon,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useNotes } from "../hooks/useNotes";
import { ActionButton } from "../types/consumerRequest";
import { RequestSummaryCardsProps } from "../types/requestSummaryCardsProps";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "../utils/constants";
import { CommunicationDialog } from "./CommunicationDialog";
import { NoteDialog } from "./NoteDialog";
import { ServiceOrderDialogue } from "./ServiceOrderDialogue";
import { useLocation } from "react-router-dom";

export function RequestSummaryCards({
  request,
  currentStatus,
  handleRejectRequest,
  remoteUtilityId,
  handleResolveRequest,
}: RequestSummaryCardsProps) {
  const REMOTE_UTILITY_ID = useRemoteUtilityId();
  const [showCommunicationDialog, setShowCommunicationDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showServiceOrderDialog, setShowServiceOrderDialog] = useState(false);
  const location = useLocation();

  // Check if URL contains "/history/"
  const isFromHistory = location.pathname.includes("/history/");
  console.log("request", request);
  const { serviceOrders } = useServiceOrders({
    remote_utility_id: getRemoteUtilityId(),
    request_type: "service-order",
    request_id: request?.id,
    // Add other filters as needed
  });
  console.log("serviceOrders", serviceOrders);
  // Use the proper useNotes hook
  const { createNote, isCreating: isCreatingNote } = useNotes({
    remoteUtilityId: Number(remoteUtilityId),
    requestId: Number(request.id),
  });

  const { mutateAsync: createCommunication } = useSendCommunication(
    request.id!
  );

  // Wrapper functions to match the expected signatures
  const createNoteWrapper = (itemId: string) => ({
    mutateAsync: async (data: any) => {
      await createNote({
        source: "consumer_support_request",
        requestId: Number(itemId),
        remoteUtilityId: Number(remoteUtilityId),
        notes: {
          note: data.notes.note,
          noteType: data.notes.noteType,
        },
      });
    },
  });

  const createCommunicationWrapper = (consumerId: string) => ({
    mutateAsync: (data: any) => createCommunication(data),
  });

  // Wrapper functions for channel hooks to match expected signatures
  const channelTypeWrapper = (remoteUtilityId: number) => useChannelType();
  const channelTemplatesWrapper = (channelType: string) =>
    useChannelTemplates(channelType);

  const hasApprovedOrInProgressSOP =
    serviceOrders?.some(
      (serviceOrder) =>
        serviceOrder?.statusDisplay === "Created" ||
        serviceOrder?.statusDisplay === "Not Assigned" ||
        serviceOrder?.statusDisplay === "In-Progress" ||
        serviceOrder?.statusDisplay === "Assigned" ||
        serviceOrder?.statusDisplay === "Overdue" ||
        serviceOrder?.statusDisplay === "Completed" ||
        serviceOrder?.statusDisplay === "Accepted" ||
        serviceOrder?.statusDisplay === "Refused" ||
        serviceOrder?.statusDisplay === "Incomplete"
    ) || false;

  console.log("hasApprovedOrInProgressSOP", hasApprovedOrInProgressSOP);
  // Quick action buttons for RequestSummaryCards
  const quickActionButtons: ActionButton[] = [
    {
      id: "send-message",
      label: "Send Response to Consumer",
      icon: MessageSquare,
      variant: "outline",
      className: "w-full bg-blue-500 text-white hover:bg-blue-600",
      onClick: () => setShowCommunicationDialog(true),
    },
    {
      id: "add-service-order",
      label: "Create Service Order",
      icon: Settings2Icon,
      variant: "default",
      disabled: currentStatus === "rejected",
      className: `w-full bg-purple-500 text-white hover:bg-purple-600 ${
        currentStatus === "rejected" ? "cursor-not-allowed disable" : ""
      }`,
      onClick: () => setShowServiceOrderDialog(true),
      condition: (status) => {
        return (
          status !==
          SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.COMPLETED]
        );
      },
    },
    {
      id: "add-internal-note",
      label: "Add Internal Note",
      icon: Plus,
      variant: "outline",
      className: "w-full bg-white text-black hover:bg-gray-100",
      onClick: () => setShowNotesDialog(true),
    },
    ...(!isFromHistory
      ? [
          {
            id: "resolve-request",
            label: "Resolve Request",
            icon: CheckCircle,
            variant: "outline" as const,
            className: "w-full bg-green-500 text-white hover:bg-green-600",
            onClick: handleResolveRequest,
            disabled: hasApprovedOrInProgressSOP,
            condition: (status) => {
  // Hide when status is completed or rejected
  return (
    status !== SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.COMPLETED] &&
    status !== SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.REJECTED]
  );
},
          },
          {
            id: "reject-request",
            label: "Reject Request",
            icon: XCircle,
            variant: "outline" as const,
            className: "w-full bg-red-500 text-white hover:bg-red-600",
            onClick: handleRejectRequest,
            disabled: hasApprovedOrInProgressSOP,
            condition: (status) => {
  // Hide when status is completed or rejected
  return (
    status !== SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.COMPLETED] &&
    status !== SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.REJECTED]
  );
},
          },
        ]
      : []),
  ];

  const relevantActions = quickActionButtons.filter(
    (action) => !action.condition || action.condition(currentStatus)
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <Card className="bg-blue-50 border-0">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium mb-3">Request Details</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Request ID</p>
              <p className="text-sm">{request.requestNo ?? "NA"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created On</p>
              <p className="text-sm">{request.dateRequested ?? "NA"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created By</p>
              <p className="text-sm">{request.requestedBy ?? "NA"}</p>
            </div>
            {request.lastUpdatedOn && (
              <div>
                <p className="text-xs text-muted-foreground">Last Updated On</p>
                <p className="text-sm">{request.lastUpdatedOn ?? "NA"}</p>
              </div>
            )}

            {request.lastUpdatedBy && (
              <div>
                <p className="text-xs text-muted-foreground">Last Updated By</p>
                <p className="text-sm">{request.lastUpdatedBy ?? "NA"}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 bg-amber-50">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium mb-3">Customer Information</h3>
          <div className="space-y-2">
            <div className="flex items-start">
              <Phone className="h-4 w-4 mr-2 text-muted-foreground mt-0.5" />
              <p className="text-sm">{request.customer.phone ?? "NA"}</p>
            </div>
            <div className="flex items-start">
              <Mail className="h-4 w-4 mr-2 text-muted-foreground mt-0.5" />
              <p className="text-sm">{request.customer.email ?? "NA"}</p>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mt-2">Category</p>
              <p className="text-sm">
                {request.customer.categoryDisplay ?? "NA"}
              </p>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mt-1">Sub Category</p>
              <p className="text-sm">
                {request.customer.subCategoryDisplay ?? "NA"}
              </p>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Service Address:
            </div>
            <p className="text-sm">{request.customer.serviceAddress ?? "NA"}</p>
            <div className="text-xs text-muted-foreground mt-1">
              Billing Address:
            </div>
            <p className="text-sm">{request.customer.billingAddress ?? "NA"}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 bg-green-50">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium mb-3">Quick Actions</h3>
          <div className="space-y-2">
            {relevantActions.map((action) => (
              <Button
                key={action.id}
                className={`${action.className} text-center` || "w-full"}
                disabled={action.disabled}
                size="sm"
                onClick={action.onClick}
              >
                <action.icon className="h-4 w-4 mr-2 text-left" />
                {action.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <NoteDialog
        isOpen={showNotesDialog}
        onClose={() => setShowNotesDialog(false)}
        itemId={request.id!}
        remoteUtilityId={Number(remoteUtilityId)}
        useSendNote={createNoteWrapper}
      />
      <CommunicationDialog
        isOpen={showCommunicationDialog}
        onClose={() => setShowCommunicationDialog(false)}
        consumerId={request.id!}
        email={request.customer.email}
        phone={request.customer.phone}
        remoteUtilityId={Number(remoteUtilityId)}
        communicationType="consumer_support_request"
        useChannelType={channelTypeWrapper}
        useChannelTemplates={channelTemplatesWrapper}
        useSendCommunication={createCommunicationWrapper}
      />
      <ServiceOrderDialogue
        consumerAccountNumber={request.customer.consumerNo || ""}
        consumerId={request.customer.id!}
        consumerName={
          request.customer.firstName + " " + request.customer.lastName
        }
        requestId={request.id!}
        remoteUtilityId={Number(REMOTE_UTILITY_ID)}
        isOpen={showServiceOrderDialog}
        onClose={() => setShowServiceOrderDialog(false)}
        onSuccess={() => setShowServiceOrderDialog(false)}
      />
    </div>
  );
}
